import clsx from 'clsx';
import {
    MultiSelectChangeEvent as PrimeMultiSelectChangeEvent,
    MultiSelectProps as PrimeMultiSelectProps,
    MultiSelect as PrimereactMultiSelect,
} from 'primereact/multiselect';
import {
    forwardRef,
    MouseEvent,
    ReactNode,
    Ref,
    SyntheticEvent,
    useImperativeHandle,
    useRef,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { getPlaceholder } from '../../_utils/get-placeholder';
import {
    buildRhfRules,
    type FieldValidate,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';
import {
    InputWrapper,
    InputWrapperSharedProps,
} from '../InputWrapper/InputWrapper';
import { useSelectOverflowBehavior } from '../../_utils/SelectOverflowBehavior';
import {
    createDefaultChipTemplate,
    resolveSelectedPillItems,
} from '../../_utils/MultiSelectChip.utils';
import { useCreatableMultiSelect } from '../../_utils/useCreatableMultiSelect';
import { MultiSelectSelectedPills } from './components/MultiSelectSelectedPills';

export type MultiSelectChangeEvent = PrimeMultiSelectChangeEvent;
export type MultiSelectSelectedValueLayout = 'external' | 'inline';

function buildChangeEvent(
    originalEvent: SyntheticEvent,
    value: unknown,
    name?: string,
): PrimeMultiSelectChangeEvent {
    return {
        originalEvent,
        value,
        target: {
            name: name ?? '',
            id: name ?? '',
            value,
        },
        stopPropagation: () => {
            originalEvent.stopPropagation();
        },
        preventDefault: () => {
            originalEvent.preventDefault();
        },
    };
}

function removeValueFromSelection(
    currentValue: unknown,
    val: unknown,
): unknown[] {
    return (Array.isArray(currentValue) ? currentValue : []).filter(
        (item) => item !== val,
    );
}

export type MultiSelectProps = PrimeMultiSelectProps & {
    name?: string;
    outlined?: boolean;
    truncateLabel?: boolean;
    wrapItems?: boolean;
    creatable?: boolean;
    onCreateOption?: (value: string) => void;
    createOptionLabel?: (query: string) => string;
    selectedValueLayout?: MultiSelectSelectedValueLayout;
    /**
     * Custom react-hook-form validation function
     */
    validate?: FieldValidate;
} & InputWrapperSharedProps;

function MultiSelectComponent(
    props: MultiSelectProps,
    ref: Ref<PrimereactMultiSelect>,
): ReactNode {
    const {
        name,
        value,
        horizontal,
        labelClassName,
        className: inputClassName,
        onBlur,
        wrapperClassName: __,
        errorClassName: ___,
        placeholder: ____,
        filter,
        labelTooltip: ______,
        bulkMode,
        outlined,
        truncateLabel,
        wrapItems,
        creatable,
        onCreateOption,
        createOptionLabel,
        selectedValueLayout,
        validate,
        'data-testid': dataTestId,
        ...restProps
    } = props;

    const innerRef = useRef<PrimereactMultiSelect>(null);

    const minLengthForFilter = 6;
    const isExternalLayout = selectedValueLayout === 'external';
    const testId = dataTestId || name;

    const placeholder = getPlaceholder({ ...props, isDropdown: true });
    const formContext = useFormContext();
    const isControlled = typeof value !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    useImperativeHandle(
        isRHFControlled ? null : ref,
        () => innerRef.current as PrimereactMultiSelect,
        [],
    );
    warnAboutRegisterSpread({
        componentName: 'MultiSelect',
        isRHFControlled,
        onChange: restProps.onChange,
        ref,
    });
    const { resolvedPanelClassName, itemTemplate } = useSelectOverflowBehavior({
        ...restProps,
        wrapItems,
    });
    const {
        options: creatableOptions,
        filter: creatableFilter,
        handleFilter,
        extractCreateSelection,
    } = useCreatableMultiSelect({
        creatable,
        options: restProps.options,
        optionLabel: restProps.optionLabel,
        optionValue: restProps.optionValue,
        createOptionLabel,
    });
    const resolvedFilter =
        filter ??
        creatableFilter ??
        (restProps.options && restProps.options.length > minLengthForFilter);
    const handleCombinedFilter: PrimeMultiSelectProps['onFilter'] = (event) => {
        handleFilter(event);
        restProps.onFilter?.(event);
    };

    if (!isRHFControlled) {
        return (
            <InputWrapper
                {...props}
                inputClassName={inputClassName}
                wrapperClassName={clsx(
                    props.wrapperClassName,
                    outlined && 'input-wrapper--outlined',
                    truncateLabel && 'multiselect--truncate-label',
                )}
            >
                <div className="ics-multiselect">
                    <div className="p-inputgroup flex-1">
                        <PrimereactMultiSelect
                            pt={{ input: { 'data-testid': testId } }}
                            className="flex grow"
                            ref={innerRef}
                            name={name}
                            placeholder={placeholder}
                            filterPlaceholder="Search.."
                            {...restProps}
                            options={creatableOptions ?? undefined}
                            filter={resolvedFilter}
                            onFilter={handleCombinedFilter}
                            itemTemplate={itemTemplate}
                            maxSelectedLabels={
                                isExternalLayout
                                    ? 0
                                    : restProps.maxSelectedLabels
                            }
                            selectedItemsLabel={
                                isExternalLayout
                                    ? restProps.selectedItemsLabel ??
                                      placeholder
                                    : restProps.selectedItemsLabel
                            }
                            selectedItemTemplate={
                                !isExternalLayout &&
                                restProps.display === 'chip' &&
                                !restProps.selectedItemTemplate
                                    ? createDefaultChipTemplate({
                                          options: restProps.options,
                                          optionLabel: restProps.optionLabel,
                                          optionValue: restProps.optionValue,
                                          onRemoveValue: (val, event) => {
                                              const nextValue =
                                                  removeValueFromSelection(
                                                      value,
                                                      val,
                                                  );

                                              props.onChange?.(
                                                  buildChangeEvent(
                                                      event,
                                                      nextValue,
                                                      name,
                                                  ),
                                              );
                                          },
                                      })
                                    : restProps.selectedItemTemplate
                            }
                            panelClassName={resolvedPanelClassName}
                            resetFilterOnHide={creatable}
                            filterDelay={creatable ? 0 : restProps.filterDelay}
                            value={value}
                            onChange={(e) => {
                                if (creatable) {
                                    const { createdValue } =
                                        extractCreateSelection(e.value);

                                    if (createdValue) {
                                        onCreateOption?.(createdValue);
                                        innerRef.current?.hide();

                                        return;
                                    }
                                }

                                if (props.onChange) {
                                    props.onChange(e);
                                }
                            }}
                        />
                    </div>
                    {isExternalLayout ? (
                        <MultiSelectSelectedPills
                            items={resolveSelectedPillItems(
                                Array.isArray(value) ? value : undefined,
                                restProps.options,
                                restProps.optionLabel,
                                restProps.optionValue,
                            )}
                            disabled={restProps.disabled}
                            onDismiss={(
                                val,
                                event: MouseEvent<HTMLButtonElement>,
                            ) => {
                                const nextValue = removeValueFromSelection(
                                    value,
                                    val,
                                );

                                props.onChange?.(
                                    buildChangeEvent(event, nextValue, name),
                                );
                            }}
                        />
                    ) : null}
                </div>
            </InputWrapper>
        );
    }

    return (
        <Controller
            name={name!}
            control={formContext!.control}
            rules={buildRhfRules({
                required: props.required,
                bulkMode,
                validate,
            })}
            render={({ field, fieldState }) => (
                <InputWrapper
                    field={field}
                    fieldState={fieldState}
                    horizontal={horizontal}
                    labelClassName={labelClassName}
                    name={name}
                    {...props}
                    inputClassName={inputClassName}
                    wrapperClassName={clsx(
                        props.wrapperClassName,
                        outlined && 'input-wrapper--outlined',
                        truncateLabel && 'multiselect--truncate-label',
                    )}
                >
                    <div className="ics-multiselect">
                        <div className="p-inputgroup flex-1">
                            <PrimereactMultiSelect
                                pt={{ input: { 'data-testid': testId } }}
                                className={clsx(
                                    'flex grow overflow-hidden',
                                    fieldState?.invalid ? 'p-invalid' : null,
                                )}
                                {...restProps}
                                {...field}
                                name={name}
                                options={creatableOptions ?? undefined}
                                filter={resolvedFilter}
                                onFilter={handleCombinedFilter}
                                filterPlaceholder="Search.."
                                placeholder={placeholder}
                                itemTemplate={itemTemplate}
                                maxSelectedLabels={
                                    isExternalLayout
                                        ? 0
                                        : restProps.maxSelectedLabels
                                }
                                selectedItemsLabel={
                                    isExternalLayout
                                        ? restProps.selectedItemsLabel ??
                                          placeholder
                                        : restProps.selectedItemsLabel
                                }
                                selectedItemTemplate={
                                    !isExternalLayout &&
                                    restProps.display === 'chip' &&
                                    !restProps.selectedItemTemplate
                                        ? createDefaultChipTemplate({
                                              options: restProps.options,
                                              optionLabel:
                                                  restProps.optionLabel,
                                              optionValue:
                                                  restProps.optionValue,
                                              onRemoveValue: (val, event) => {
                                                  const currentValue =
                                                      field?.value ||
                                                      props?.value ||
                                                      [];
                                                  const nextValue =
                                                      removeValueFromSelection(
                                                          currentValue,
                                                          val,
                                                      );
                                                  const nextEvent =
                                                      buildChangeEvent(
                                                          event,
                                                          nextValue,
                                                          name,
                                                      );

                                                  field.onChange(nextEvent);

                                                  if (props.onChange) {
                                                      props.onChange(nextEvent);
                                                  }
                                              },
                                          })
                                        : restProps.selectedItemTemplate
                                }
                                panelClassName={resolvedPanelClassName}
                                resetFilterOnHide={creatable}
                                filterDelay={
                                    creatable ? 0 : restProps.filterDelay
                                }
                                value={field?.value || props?.value || []}
                                onChange={(e) => {
                                    if (creatable) {
                                        const { createdValue } =
                                            extractCreateSelection(e.value);

                                        if (createdValue) {
                                            onCreateOption?.(createdValue);
                                            innerRef.current?.hide();

                                            return;
                                        }
                                    }

                                    field.onChange(e);

                                    if (props.onChange) {
                                        props.onChange(e);
                                    }
                                }}
                                onBlur={(event) => {
                                    field.onBlur();
                                    onBlur?.(event);
                                }}
                                ref={mergeRefs(innerRef, field.ref)}
                            />
                        </div>
                        {isExternalLayout ? (
                            <MultiSelectSelectedPills
                                items={resolveSelectedPillItems(
                                    Array.isArray(field?.value || props?.value)
                                        ? field?.value || props?.value
                                        : undefined,
                                    restProps.options,
                                    restProps.optionLabel,
                                    restProps.optionValue,
                                )}
                                disabled={restProps.disabled}
                                onDismiss={(
                                    val,
                                    event: MouseEvent<HTMLButtonElement>,
                                ) => {
                                    const currentValue =
                                        field?.value || props?.value || [];
                                    const nextValue = removeValueFromSelection(
                                        currentValue,
                                        val,
                                    );
                                    const nextEvent = buildChangeEvent(
                                        event,
                                        nextValue,
                                        name,
                                    );

                                    field.onChange(nextEvent);

                                    if (props.onChange) {
                                        props.onChange(nextEvent);
                                    }
                                }}
                            />
                        ) : null}
                    </div>
                </InputWrapper>
            )}
        />
    );
}

export const MultiSelect = forwardRef(MultiSelectComponent);
